import { Button, Plus, useIsScrolled } from '../../modules/design-system';

export interface ShippingHeaderProps {
  /** Fired by the "+ Add Delivery" CTA — opens the create-delivery flow.
   *  Omitted when shipping is disabled for the session, which hides the CTA. */
  onAddDelivery?: () => void;
}

/**
 * Standalone Shipping header — left "Shipping" title, right "+ Add Delivery"
 * CTA. No Home button and no tab switcher (the page is single-purpose). Built
 * on the same glass / scrolled-blur pattern as `MyPacksHeader` so it sits in
 * the same Design Constructor scope (`--co-MyPacksHeader-*`).
 *
 * Unlike My Packs — where Add Delivery is a desktop-only right-slot control —
 * here it's the page's only action, so it stays visible at every width
 * (compact icon-led pill on mobile, full label ≥600px).
 */
export function ShippingHeader({ onAddDelivery }: ShippingHeaderProps) {
  const scrolled = useIsScrolled(8);
  return (
    <header
      data-co-component="MyPacksHeader"
      data-scrolled={scrolled || undefined}
      className={
        'sticky top-0 z-30 header-safe-top ' +
        'transition-[background-color,backdrop-filter,border-color] duration-200 ease-out ' +
        (scrolled
          ? 'bg-black/40 backdrop-blur-md border-b border-white/10 '
          : 'border-b border-transparent ')
      }
      style={{
        fontFamily: 'var(--co-MyPacksHeader-fontFamily), Inter, system-ui, sans-serif',
      }}
    >
      <div
        className="
          mx-auto flex w-full max-w-350 items-center justify-between gap-3
          px-4 py-4 @[600px]:px-6 @[600px]:py-5
        "
      >
        <h1
          className="text-[18px] font-bold tracking-[-0.3px] @[600px]:text-[22px]"
          style={{ color: 'var(--co-Tabs-tabIdleText, #FFFFFF)' }}
        >
          Shipping
        </h1>

        {onAddDelivery && <AddDeliveryButton onClick={onAddDelivery} />}
      </div>
    </header>
  );
}

function AddDeliveryButton({ onClick }: { onClick: () => void }) {
  return (
    <>
      {/* Mobile — compact pill so the title keeps its room. */}
      <div className="@[600px]:hidden">
        <Button
          variant="glass-frosted"
          size="md"
          onClick={onClick}
          rightIcon={<Plus size={20} strokeWidth={2.5} />}
        >
          Add
        </Button>
      </div>

      {/* ≥600px — full label. */}
      <div className="hidden @[600px]:block">
        <Button
          variant="glass-frosted"
          size="lg"
          onClick={onClick}
          rightIcon={<Plus size={22} strokeWidth={2.5} />}
        >
          Add Delivery
        </Button>
      </div>
    </>
  );
}
